import React from 'react';

const ALLOWED_TAGS = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'br',
  'strong',
  'em',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'code',
  'a',
  'span',
  'div'
]);

const VOID_TAGS = new Set(['br']);

const getElementProps = (el: Element): Record<string, string | undefined> => {
  const props: Record<string, string | undefined> = {};
  const className = el.getAttribute('class');
  if (className) props.className = className;

  if (el.tagName.toLowerCase() === 'a') {
    const href = el.getAttribute('href');
    if (href) props.href = href;
    const target = el.getAttribute('target');
    if (target) props.target = target;
    props.rel = 'noopener noreferrer';
  }

  return props;
};

const nodeToReact = (node: ChildNode, key: number): React.ReactNode => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';

    return text.length > 0 ? text : null;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const children = Array.from(el.childNodes)
    .map((child, i) => nodeToReact(child, i))
    .filter((child) => child !== null && child !== '');

  if (!ALLOWED_TAGS.has(tag)) {
    return children.length > 0 ? (
      <React.Fragment key={key}>{children}</React.Fragment>
    ) : null;
  }

  if (VOID_TAGS.has(tag)) {
    return React.createElement(tag, {key, ...getElementProps(el)});
  }

  return React.createElement(tag, {key, ...getElementProps(el)}, ...children);
};

const hasVisibleContent = (nodes: React.ReactNode[]): boolean => {
  for (const node of nodes) {
    if (node === null || node === undefined || node === false) continue;
    if (typeof node === 'string') {
      if (node.trim().length > 0) return true;
      continue;
    }
    if (typeof node === 'number') return true;
    if (Array.isArray(node)) {
      if (hasVisibleContent(node)) return true;
      continue;
    }

    return true;
  }

  return false;
};

/** Parse framework WYSIWYG HTML into React elements (no dangerouslySetInnerHTML). */
export const parseHtmlAnswerToReact = (html: string): React.ReactNode => {
  const trimmed = (html || '').trim();
  if (!trimmed) return null;

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  const body = doc.body;
  const nodes = Array.from(body.childNodes)
    .map((child, i) => nodeToReact(child, i))
    .filter((child) => child !== null && child !== '');

  if (!hasVisibleContent(nodes)) return null;

  return nodes.length === 1 ? nodes[0] : nodes;
};
